import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Pane, Text, Spinner, Alert, Textarea, TextInput, Button,
} from 'evergreen-ui';
import { fetchContent, saveContent } from '../services/rubricaService';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedText = ({ text, term, activeIndex, containerRef }) => {
  useEffect(() => {
    if (!term.trim()) return;
    const el = containerRef.current?.querySelector(`[data-match="${activeIndex}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex, term, containerRef]);

  if (!term.trim()) {
    return (
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0 }}>
        {text}
      </pre>
    );
  }

  const parts = [];
  const re = new RegExp(escapeRe(term), 'gi');
  let last = 0;
  let match;
  let i = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(<span key={`t${i}`}>{text.slice(last, match.index)}</span>);
    }
    parts.push(
      <mark
        key={`m${i}`}
        data-match={i}
        style={{
          backgroundColor: i === activeIndex ? '#ff9500' : '#ffe066',
          borderRadius: 2,
          padding: '0 1px',
        }}
      >
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
    i++;
  }
  if (last < text.length) parts.push(<span key="tend">{text.slice(last)}</span>);

  return (
    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0 }}>
      {parts}
    </pre>
  );
};

const Rubrica = () => {
  const [content, setContent] = useState('');
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchContent();
      setContent(data.content ?? '');
    } catch {
      setError('Errore nel caricamento del documento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const matchCount = useMemo(() => {
    if (!searchTerm.trim() || !content) return 0;
    return (content.match(new RegExp(escapeRe(searchTerm), 'gi')) ?? []).length;
  }, [content, searchTerm]);

  useEffect(() => { setActiveIndex(0); }, [searchTerm]);

  const handleEdit = () => {
    setEditContent(content);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContent(editContent);
      setContent(editContent);
      setIsEditing(false);
    } catch {
      setError('Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditContent('');
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" padding={32}>
        <Spinner />
      </Pane>
    );
  }

  return (
    <Pane maxWidth={900}>
      {error && (
        <Alert intent="danger" title={error} marginBottom={16} onRemove={() => setError(null)} />
      )}

      {/* Barra azioni */}
      <Pane display="flex" alignItems="center" marginBottom={16}>
        {!isEditing && (
          <>
            <TextInput
              placeholder="Cerca nel documento..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              width={260}
              marginRight={8}
            />
            {searchTerm.trim() && matchCount > 0 && (
              <>
                <Text size={300} color="muted" marginRight={8}>
                  {activeIndex + 1} / {matchCount}
                </Text>
                <Button
                  height={28}
                  marginRight={4}
                  onClick={() => setActiveIndex(i => (i - 1 + matchCount) % matchCount)}
                >
                  ↑
                </Button>
                <Button
                  height={28}
                  marginRight={16}
                  onClick={() => setActiveIndex(i => (i + 1) % matchCount)}
                >
                  ↓
                </Button>
              </>
            )}
            {searchTerm.trim() && matchCount === 0 && (
              <Text size={300} color="muted" marginRight={16}>Nessun risultato</Text>
            )}
          </>
        )}

        <Pane display="flex" marginLeft="auto">
          {isEditing ? (
            <>
              <Button appearance="primary" onClick={handleSave} isLoading={saving} marginRight={8}>
                Salva
              </Button>
              <Button onClick={handleCancel}>Annulla</Button>
            </>
          ) : (
            <Button appearance="primary" onClick={handleEdit}>Modifica</Button>
          )}
        </Pane>
      </Pane>

      {/* Documento */}
      {isEditing ? (
        <Textarea
          value={editContent}
          onChange={e => setEditContent(e.target.value)}
          width="100%"
          height={600}
          style={{ fontFamily: 'inherit', fontSize: 14, resize: 'vertical' }}
          autoFocus
        />
      ) : (
        <Pane
          ref={containerRef}
          border="default"
          borderRadius={4}
          padding={16}
          minHeight={600}
          background="white"
          overflow="auto"
          style={{ fontSize: 14, lineHeight: 1.6 }}
        >
          {content.trim() ? (
            <HighlightedText
              text={content}
              term={searchTerm}
              activeIndex={activeIndex}
              containerRef={containerRef}
            />
          ) : (
            <Text color="muted" fontStyle="italic">
              Il documento è vuoto. Clicca "Modifica" per iniziare a scrivere.
            </Text>
          )}
        </Pane>
      )}
    </Pane>
  );
};

export default Rubrica;
